import { $createListItemNode, $createListNode } from '@lexical/list';
import type { LexicalEditor, SerializedEditorState } from 'lexical';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';
import { useAiFeedback } from '../../ai-feedback/hooks/use-ai-feedback.ts';
import type { AiFeedbackType } from '../../ai-feedback/types/ai-feedback-types.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AIWritingState } from '../components/lexical.tsx';
import type { EntityType } from '../types/entity-types.ts';

export type AIActionType = 'request' | 'regenerate' | 'accept' | 'reject' | 'thumbs_up' | 'thumbs_down';

export type UseImproveWritingOptions = {
    entityType?: EntityType;
    entityId?: string;
    editor: LexicalEditor;
    setAIWritingState: (state: AIWritingState) => void;
    onChange?: (value: SerializedEditorState) => void;
    onAIInteraction?: (action: AIActionType, sessionId: string) => void;
};

export type UseImproveWriting = {
    improveWriting: () => Promise<void>;
    cancelImproveWriting: () => void;
    acceptPreview: () => void;
    rejectPreview: () => void;
    rateFeedback: (rating: 'thumbs_up' | 'thumbs_down') => void;
    hasRated: boolean;
};

/**
 * Hook that provides common functionality for improving writing with AI.
 * Handles text extraction, API calls, and rendering structured responses in Lexical editor.
 */
export function useImproveWriting({
    entityType = 'note',
    entityId,
    editor,
    setAIWritingState,
    onChange,
    onAIInteraction,
}: UseImproveWritingOptions): UseImproveWriting {
    const { t } = useLocale();
    const sendEvent = useAnalytics();
    const suggestMutation = api.platoUser.command.improveWriting.suggest.useMutation();

    const originalEditorStateRef = useRef<SerializedEditorState | undefined>(undefined);
    const regenerateAttemptsRef = useRef<number>(0);
    const sessionIdRef = useRef<string | undefined>(undefined);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [hasRated, setHasRated] = useState(false);
    const { sendFeedback } = useAiFeedback();

    /**
     * Helper to send analytics events with consistent payload structure
     */
    const sendAnalyticsEvent = useCallback(
        (action: 'request' | 'accept' | 'reject' | 'regenerate') => {
            if (!sessionIdRef.current) {
                return;
            }

            const eventName = `ai_text_${action}_formatting` as const;
            const payload = {
                entity_type: entityType,
                entity_id: entityId ?? undefined,
                session_id: sessionIdRef.current,
            };

            sendEvent(eventName, payload);
        },
        [sendEvent, entityType, entityId],
    );

    /**
     * Common helper to send feedback with consistent payload structure
     */
    const sendFeedbackHelper = useCallback(
        (feedbackType: AiFeedbackType) => {
            if (sessionIdRef.current) {
                sendFeedback({
                    feedbackType,
                    sessionId: sessionIdRef.current,
                    entityType,
                    payload: {
                        entityId: entityId ?? undefined,
                        metadata: {
                            ...suggestMutation.data?.metadata,
                            id: suggestMutation.data?.id ?? '',
                        },
                    },
                }).catch(() => {
                    // Ignore errors from feedback submission to avoid impacting user experience
                });
            }
        },
        [sendFeedback, entityType, entityId, suggestMutation.data],
    );

    /**
     * Restores the original editor state
     */
    const restoreOriginalState = useCallback(() => {
        if (originalEditorStateRef.current) {
            const editorState = editor.parseEditorState(originalEditorStateRef.current);
            editor.setEditorState(editorState);
        }
    }, [editor]);

    /**
     * Accepts the current preview content and clears snapshots
     */
    const acceptPreview = useCallback(() => {
        sendAnalyticsEvent('accept');
        sendFeedbackHelper('accept');

        if (onAIInteraction && sessionIdRef.current) {
            onAIInteraction('accept', sessionIdRef.current);
        }

        if (onChange) {
            const currentState = editor.getEditorState();
            onChange(currentState.toJSON());
        }

        setAIWritingState('idle');

        originalEditorStateRef.current = undefined;
        regenerateAttemptsRef.current = 0;
        sessionIdRef.current = undefined;
    }, [setAIWritingState, onChange, editor, sendFeedbackHelper, sendAnalyticsEvent, onAIInteraction]);

    /**
     * Rejects the preview and restores original content
     */
    const rejectPreview = useCallback(() => {
        sendAnalyticsEvent('reject');
        sendFeedbackHelper('reject');

        if (onAIInteraction && sessionIdRef.current) {
            onAIInteraction('reject', sessionIdRef.current);
        }

        restoreOriginalState();
        setAIWritingState('idle');

        originalEditorStateRef.current = undefined;
        regenerateAttemptsRef.current = 0;
        sessionIdRef.current = undefined;
    }, [restoreOriginalState, setAIWritingState, sendFeedbackHelper, sendAnalyticsEvent, onAIInteraction]);

    /**
     * Rates the AI feedback with thumbs up or thumbs down
     */
    const rateFeedback = useCallback(
        (rating: 'thumbs_up' | 'thumbs_down') => {
            sendFeedbackHelper(rating);
            setHasRated(true);

            if (onAIInteraction && sessionIdRef.current) {
                onAIInteraction(rating, sessionIdRef.current);
            }
        },
        [sendFeedbackHelper, onAIInteraction],
    );

    /**
     * Helper to render a section with a heading and bullet list
     */
    function renderSection(root: ReturnType<typeof $getRoot>, heading: string, items: string[]): void {
        const p = $createParagraphNode();
        const tn = $createTextNode(heading);
        tn.setFormat('bold');
        p.append(tn);
        root.append(p);

        const list = $createListNode('bullet');
        for (const text of items) {
            const li = $createListItemNode();
            const p = $createParagraphNode();
            p.append($createTextNode(text));
            li.append(p);
            list.append(li);
        }
        root.append(list);
    }

    /**
     * Renders the structured AI response into the Lexical editor
     */
    function renderResponseInEditor(response: typeof suggestMutation.data): void {
        editor.update(() => {
            if (!response) {
                return;
            }

            const hasValidContent = (arr: string[] | null | undefined): arr is string[] => {
                return Array.isArray(arr) && arr.length > 0 && arr.join('').trim() !== '';
            };

            // Check if there's any valid content to render
            const hasContent =
                hasValidContent(response.summary) ||
                hasValidContent(response.opportunities) ||
                hasValidContent(response.feedback) ||
                hasValidContent(response.next_steps);

            if (!hasContent) {
                return;
            }

            const root = $getRoot();
            root.clear();

            // Render each section if it has content
            if (hasValidContent(response.summary)) {
                renderSection(root, t('rich_text_editor__heading_summary'), response.summary);
            }

            if (hasValidContent(response.opportunities)) {
                renderSection(root, t('rich_text_editor__heading_opportunities'), response.opportunities);
            }

            if (hasValidContent(response.feedback)) {
                renderSection(root, t('rich_text_editor__heading_feedback'), response.feedback);
            }

            if (hasValidContent(response.next_steps)) {
                renderSection(root, t('rich_text_editor__heading_next_steps'), response.next_steps);
            }
        });
    }

    const improveWriting = async (): Promise<void> => {
        const textToImprove = editor.getEditorState().read(() => {
            const root = $getRoot();

            return root.getTextContent();
        });

        if (!textToImprove.trim()) {
            return;
        }

        try {
            setHasRated(false);

            abortControllerRef.current?.abort();
            abortControllerRef.current = new AbortController();

            const { signal } = abortControllerRef.current;
            setAIWritingState('improve-loading');

            // If this is a regeneration (sessionId exists), send regenerate feedback and analytics
            if (sessionIdRef.current) {
                regenerateAttemptsRef.current += 1;
                sendAnalyticsEvent('regenerate');
                sendFeedbackHelper('regenerate');

                if (onAIInteraction) {
                    onAIInteraction('regenerate', sessionIdRef.current);
                }
            } else {
                sessionIdRef.current = uuidv4();
                regenerateAttemptsRef.current = 0;
                sendAnalyticsEvent('request');

                if (onAIInteraction && sessionIdRef.current) {
                    onAIInteraction('request', sessionIdRef.current);
                }
            }

            originalEditorStateRef.current ??= editor.getEditorState().toJSON();

            const response = await suggestMutation.mutateAsync({
                entityType,
                entityId: entityId ?? '',
                text: textToImprove,
            });

            if (signal.aborted) {
                return;
            }

            renderResponseInEditor(response);
            setAIWritingState('improve-preview');
        } catch {
            // Reset to idle state on error
            setAIWritingState('idle');
            originalEditorStateRef.current = undefined;
        }
    };

    const cancelImproveWriting = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;

        sessionIdRef.current = undefined;
        setAIWritingState('idle');
    }, [setAIWritingState]);

    return {
        improveWriting,
        cancelImproveWriting,
        acceptPreview,
        rejectPreview,
        rateFeedback,
        hasRated,
    };
}
